import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { AppConfig } from '../../shared/models/app-config.model';
import { Certificate, CertificateError } from '../../shared/models/certificate.model';
import { ConfigInitService } from '../../shared/services/config-init.service';

export const MISSING_ID_ERROR = 'No certificate ID found in URL';
export const CONFIG_ERROR = 'The certificate service is unavailable. Please try again later.';
export const NETWORK_ERROR = 'The certificate service could not be reached. Please check your connection and try again.';
export const UNKNOWN_ERROR = 'The certificate could not be verified. Please try again later.';

/**
 * Validates the certificate whose hash is in the `:id/validate` route and renders it, or the API's error message
 */
@Component({
  selector: 'app-certificate-verification',
  standalone: true,
  imports: [CommonModule, LoadingComponent],
  templateUrl: './certificate-verification.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './certificate-verification.component.scss'
})
export class CertificateVerificationComponent implements OnInit {
  title = 'Programme Completion Certificate - SPHYNX Cyber Range';

  data?: Certificate;
  error: string | null = null;
  isLoading = true;

  private httpClient = inject(HttpClient);
  private activatedRoute = inject(ActivatedRoute);
  private configInitService = inject(ConfigInitService);

  /**
   * Reads the certificate hash from the route and requests its validation from the Cyber Range API
   */
  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');

    if (id) {
      this.configInitService
        .getConfig()
        .then((configData: AppConfig) => {
          this.httpClient.get<Certificate>(configData.cyberRangeVerifyCertApiUrl + id + '/validate').subscribe({
            next: (response) => {
              this.data = response;
              this.isLoading = false;
            },
            error: (error: HttpErrorResponse) => {
              this.error = this.errorMessage(error);
              this.isLoading = false;
            }
          });
        })
        .catch((reason: unknown) => {
          // Without this the rejection goes unhandled and the spinner never stops; the reason names the missing file for operators
          console.error(reason);
          this.error = CONFIG_ERROR;
          this.isLoading = false;
        });
    } else {
      this.error = MISSING_ID_ERROR;
      this.isLoading = false;
    }
  }

  /**
   * Picks the message to show for a failed validation request
   *
   * @param {HttpErrorResponse} error The failed response
   *
   * @returns {string} The API's own message when the body carries one, otherwise a message for a network failure (status 0) or any other failure
   */
  private errorMessage(error: HttpErrorResponse): string {
    const message = (error.error as Partial<CertificateError> | null)?.error;

    if (typeof message === 'string' && message) {
      return message;
    }

    return error.status === 0 ? NETWORK_ERROR : UNKNOWN_ERROR;
  }
}
